import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useParams, Link } from "react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Dropdown } from "react-bootstrap";

import { setItemToStorage } from "../../../../system";
import {
  FILTER_LABEL,
  pathConstant,
  loginConstants,
  managerPerformanceConstants,
  averageCardTitleConstants,
} from "../../constants";
import {
  useSpinner,
  Spinner,
  DataTable,
  StatusCard,
  SearchComponent,
  ChangeComponent,
  AverageRatingCard,
  compareNumericString,
  calculateChangeColumnValueForSentimentRating,
  calculateChangeColumnValueForTransactionRating,
} from "../../../../shared";
import {
  brandPerformanceConstants,
  brandProfileConstants,
  tableColumns,
} from "../../../shared-screens/constants";

const {
  ALL_MANAGER_RESPONSE_DATA,
  STORES_AVERAGE_SENTIMENT_AND_TRANSACTION_RATING,
} = managerPerformanceConstants;
const { GET_AVERAGE_RATING_RESPONSES, GET_RECORD_PAGINATION_RESPONSES } =
  brandPerformanceConstants;
const { AVERAGE_TRANSACTION_RATING, AVERAGE_SENTIMENT_RATING } =
  averageCardTitleConstants;

const Performance = (props) => {
  const {
    getData,
    sorting,
    dayCounts,
    exportFunc,
    searchString,
    setSearchString,
    selectedOption,
    totalResponses,
    totalRestaurants,
    userResponsesData,
    userResponseDataPagination,
    avgSentimentAndTransactionRatingData,
    openExportCustomerDataConfirmationModal,
  } = props;

  const { managerId } = useParams();

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Handle store name click
  const handleClickStoreName = (restaurantData) => {
    setItemToStorage(loginConstants.LOCATION_ID, restaurantData.id);

    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify({ name: pathConstant.MANAGER_PERFORMANCE }),
    );
  };

  useEffect(() => {
    if (userResponsesData && userResponsesData.length > 0) {
      calculateChangeColumnValueForTransactionRating(userResponsesData);
      calculateChangeColumnValueForSentimentRating(userResponsesData);
    }
    // eslint-disable-next-line
  }, [userResponsesData]);

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const restaurantData = row.original;
      const restaurantName = restaurantData.name;
      const path = `/dashboard/${managerId}/manager-performance/store-performance/${restaurantData.id}`;
      if (restaurantName) {
        return (
          <Link
            className="table-links"
            to={path}
            onClick={() => handleClickStoreName(restaurantData)}
            state={{ name: pathConstant.MANAGER_PERFORMANCE }}
          >
            {restaurantName}
          </Link>
        );
      } else {
        return <span />;
      }
    },
    // eslint-disable-next-line
    [managerId],
  );

  const headerTransactionRatingRenderer = useCallback(
    () => {
      return <span className="text-secondary">AVERAGE TRANSACTION RATING</span>;
    },
    // eslint-disable-next-line
    [],
  );

  const headerSentimentRatingRenderer = useCallback(
    () => {
      return <span className="text-secondary">AVERAGE SENTIMENT RATING</span>;
    },
    // eslint-disable-next-line
    [],
  );

  const headerSurveyCompletionDateRenderer = useCallback(
    () => {
      return (
        <span className="text-secondary">
          PREVIOUS {dayCounts === 1 ? `${dayCounts} DAY` : `${dayCounts} DAYS`}
        </span>
      );
    },
    // eslint-disable-next-line
    [dayCounts],
  );

  const cellTransactionChangeRenderer = useCallback(
    ({ row }) => {
      const { transactionChange, isTransactionChangeGreaterThanZero } =
        row.original;
      return (
        <ChangeComponent
          result={transactionChange}
          isValueGreaterThanZero={isTransactionChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const cellSentimentChangeRenderer = useCallback(
    ({ row }) => {
      const { sentimentChange, isSentimentChangeGreaterThanZero } =
        row.original;
      return (
        <ChangeComponent
          result={sentimentChange}
          isValueGreaterThanZero={isSentimentChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const columnHelper = createColumnHelper();
  //Static headers for data table
  const Columns = [
    columnHelper.accessor("name", {
      header: () => "STORE NAME",
      cell: cellNameRenderer,
    }),
    columnHelper.accessor("recordCount", {
      header: () => "TOTAL RESPONSES",
    }),
    columnHelper.accessor("transactionRating", {
      header: headerTransactionRatingRenderer,
    }),
    columnHelper.accessor("previousTransactionRating", {
      header: headerSurveyCompletionDateRenderer,
      minSize: tableColumns.SIZE_200,
      cell: (row) => row.getValue() || "-",
    }),
    columnHelper.accessor("transactionChange", {
      header: () => "TRANSACTION CHANGE",
      enableSorting: true,
      cell: cellTransactionChangeRenderer,
      sortingFn: compareNumericString,
    }),
    columnHelper.accessor("sentimentRating", {
      header: headerSentimentRatingRenderer,
    }),
    columnHelper.accessor("previousSentimentRating", {
      header: headerSurveyCompletionDateRenderer,
      minSize: tableColumns.SIZE_200,
      cell: (row) => row.getValue() || "-",
    }),
    columnHelper.accessor("sentimentChange", {
      header: () => "SENTIMENT CHANGE",
      enableSorting: true,
      cell: cellSentimentChangeRenderer,
      sortingFn: compareNumericString,
    }),
  ];

  return (
    <>
      {(spinner(ALL_MANAGER_RESPONSE_DATA) ||
        spinner(STORES_AVERAGE_SENTIMENT_AND_TRANSACTION_RATING) ||
        spinner(GET_AVERAGE_RATING_RESPONSES) ||
        spinner(GET_RECORD_PAGINATION_RESPONSES)) && <Spinner />}
      <div className="container-fluid pb-3">
        <div className=" d-md-flex pb-3 justify-content-between">
          <StatusCard
            title="Total Stores"
            data={totalRestaurants}
            classNames="me-3 mb-3 mb-md-0"
            bodyClassNames="p-3 mb-0"
          />
          <StatusCard
            title={
              !selectedOption.length ||
              selectedOption.find((item) => item.label === FILTER_LABEL.ALL)
                ? "Total Responses"
                : `Total ${selectedOption.map(
                    (item) => item.value,
                  )} Star Responses`
            }
            data={totalResponses}
            classNames=""
            bodyClassNames="p-3 mb-0"
          />
        </div>
        <div className=" d-md-flex mb-3 justify-content-between mt-2">
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="me-0 me-md-3 mb-3 mb-md-0"
            title={AVERAGE_TRANSACTION_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData.averageTransactionRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData.previousAverageTransactionRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData.averageTransaction
            }
          />
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="mb-md-0 mb-3"
            title={AVERAGE_SENTIMENT_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData.averageSentimentRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData.previousAverageSentimentRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData.averageSentiment
                ? avgSentimentAndTransactionRatingData.averageSentiment
                : []
            }
          />
        </div>
        <div className="pb-3 text-end">
          <Dropdown>
            <Dropdown.Toggle
              id="export-dropdown"
              data-testid="export-button"
              className="green-background-color green-border"
              disabled={!userResponseDataPagination.totalElements}
            >
              Export
            </Dropdown.Toggle>

            <Dropdown.Menu className="text-center shadow">
              <Dropdown.Item onClick={exportFunc}>Data Table</Dropdown.Item>
              <Dropdown.Item onClick={openExportCustomerDataConfirmationModal}>
                Detailed Customer Survey Data
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="notification-form card shadow">
          <div className="card-body px-0">
            <div className="d-flex justify-content-between">
              <h5 className=" responses px-4">Total Stores</h5>
              <SearchComponent
                searchString={searchString}
                setSearchString={setSearchString}
              />
            </div>
            <div className="mt-3">
              <DataTable
                columns={Columns}
                data={userResponsesData}
                totalRows={userResponseDataPagination.totalElements}
                numberOfRowsPerPage={userResponseDataPagination.size}
                pageNumber={userResponseDataPagination.number + 1}
                pageSize={userResponseDataPagination.size}
                sortedBy={sorting}
                onPageChange={(pageNum) =>
                  getData(pageNum, null, userResponseDataPagination.size)
                }
                onColumnSort={(sortBy) =>
                  getData(
                    userResponseDataPagination.number,
                    sortBy,
                    userResponseDataPagination.size,
                  )
                }
                onSizeChange={(size, pageNum) => {
                  getData(pageNum, null, size);
                }}
                isPaginationDropDownEnable={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, null)(Performance);
